import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import {
    CellProps,
    ReactColumn,
    SortingRule,
    TableComponent
} from '../components/table';
import { CommunityDto, CommunityVisibility } from '@api/ChallengeClient';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { PageHeader } from '@components/page-header';
import { Filters } from '../components/filters';
import { useCommunityStore } from './store';
import { injectTSDI } from '../core/tsdi';
import { I18n } from '../core/i18n';
import { links } from '../core/router';
import { DeleteCommunityDialog } from './delete-community-dialog';

export const CommunityList = () => {
    const { formatShortDate } = injectTSDI(I18n);
    const navigate = useNavigate();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedCommunity, setSelectedCommunity] =
        useState<CommunityDto | null>(null);

    const {
        communities,
        visibilityFilter,
        sorting,
        setSorting,
        setVisibilityFilter,
        setSearchTerm,
        searchParams,
        dispatch
    } = useCommunityStore();

    const onSortingChange = (sorting?: SortingRule) => {
        if (sorting) {
            setSorting(sorting);
        }
    };

    const clearFilters = () => {
        setVisibilityFilter([]);
    };

    const columns: ReactColumn<CommunityDto>[] = [
        {
            Header: 'Name',
            accessor: 'name',
            Cell: ({
                row: {
                    original: { id, name }
                }
            }: CellProps<CommunityDto, string>) => (
                <Link
                    to={links.communityDetails(id.toString())}
                    className="text-sm font-semibold hover:underline"
                >
                    {name}
                </Link>
            )
        },
        {
            Header: 'Tenant ID',
            accessor: 'tenantId'
        },
        {
            Header: 'Created Date',
            accessor: 'createdDate',
            Cell: ({
                row: {
                    original: { createdDate }
                }
            }: CellProps<CommunityDto, string>) => (
                <span className="text-sm">{formatShortDate(createdDate)}</span>
            )
        },
        {
            Header: 'Type',
            accessor: 'type',
            disableSortBy: true,
            Cell: ({ value }: CellProps<CommunityDto, string>) => (
                <Badge variant="outline">{value}</Badge>
            )
        },
        {
            Header: 'Visibility',
            accessor: 'visibility',
            disableSortBy: true,
            Cell: ({ value }: CellProps<CommunityDto, CommunityVisibility>) => (
                <Badge
                    variant={
                        value === CommunityVisibility.PUBLIC
                            ? 'success'
                            : 'outline'
                    }
                >
                    {value}
                </Badge>
            )
        },
        {
            Header: 'Status',
            accessor: 'approved',
            disableSortBy: true,
            Cell: ({ value }: CellProps<CommunityDto, boolean>) => (
                <Badge variant={value ? 'success' : 'warning'}>
                    {value ? 'APPROVED' : 'NOT APPROVED'}
                </Badge>
            )
        },
        {
            Header: '',
            id: 'actions',
            disableSortBy: true,
            width: 50,
            Cell: () => null
        }
    ];

    return (
        <>
            <PageHeader
                title="Communities"
                actions={
                    <Button onClick={() => navigate(links.createCommunity())}>
                        <Plus />
                        Create
                    </Button>
                }
            />
            <div className="mb-4 flex flex-wrap items-end gap-3">
                <Filters<CommunityVisibility, true>
                    multiple
                    label="Visibility"
                    value={visibilityFilter}
                    options={[
                        {
                            label: 'Public',
                            value: CommunityVisibility.PUBLIC
                        },
                        {
                            label: 'Private',
                            value: CommunityVisibility.PRIVATE
                        }
                    ]}
                    onChange={(val) => void setVisibilityFilter(val)}
                    width={200}
                />
                <Button variant="ghost" onClick={() => void clearFilters()}>
                    Clear
                    <X />
                </Button>
            </div>

            <TableComponent
                columns={columns}
                items={communities}
                onRowClick={(community) =>
                    void navigate(links.communityDetails(String(community.id)))
                }
                onSortingChange={onSortingChange}
                sorting={sorting as SortingRule | undefined}
                onSearchChange={setSearchTerm}
                searchPlaceholder="Search communities..."
                onChangePage={(page) => dispatch({ type: 'page', page })}
                onPageSizeChange={(size) => dispatch({ type: 'size', size })}
                contextMenu={[
                    {
                        label: 'Details',
                        action: (community) => {
                            navigate(links.communityDetails(community.id));
                        }
                    },
                    {
                        label: 'Delete',
                        action: (community) => {
                            setSelectedCommunity(community);
                            setDeleteDialogOpen(true);
                        }
                    }
                ]}
            />

            <DeleteCommunityDialog
                open={deleteDialogOpen}
                onClose={() => {
                    setDeleteDialogOpen(false);
                    setSelectedCommunity(null);
                }}
                community={selectedCommunity}
                onSuccess={() => {
                    setDeleteDialogOpen(false);
                    setSelectedCommunity(null);
                    communities.request([searchParams, visibilityFilter]);
                }}
            />
        </>
    );
};
